import { logout, me } from "@/services/auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { createContext, useCallback, useEffect, useMemo, useReducer } from "react";
import { updateAmbientService } from "@/services/user";
import { clearSession } from "./utils";

const initialState = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'SET_USER') {
    return {
      ...state,
      isInitialized: true,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'SET_VIEW_PAGE_AS') {
    return {
      ...state,
      user: {
        ...state.user,
        viewPageAs: action.payload,
      },
    };
  }

  return state;
}

export const SessionContext = createContext(null);

SessionProvider.propTypes = {
  children: PropTypes.node,
};

export function SessionProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { refetch } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return me();
      } catch (error) {
        return null;
      }
    },
    enabled: state.isAuthenticated,
    refetchInterval: 120000,
    retry: false,
  });

  const { mutate: logoutMutation } = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearSession()
      dispatch({
        type: 'LOGOUT',
      });
    }
  })

  const initialize = useCallback(
    async () => {
      try {
        const { data: user } = await refetch();

        if (!user) {
          throw new Error('Credenciais inválidas');
        }

        dispatch({
          type: 'SET_USER',
          payload: { user },
        });
      } catch (error) {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    },
    [refetch]
  );

  const updateAmbient = useCallback(async ({ viewPageAs }) => {
    updateAmbientService({ viewPageAs });

    dispatch({
      type: 'SET_VIEW_PAGE_AS',
      payload: viewPageAs,
    });
  }, []);

  useEffect(() => {
    initialize()
  }, [initialize])

  function formatSSOUrl(path) {
    const baseUrl = process.env.REACT_APP_SSO_BASE_URL;
    const redirectUrl = process.env.REACT_APP_SSO_REDIRECT_URL;

    if (!baseUrl || !redirectUrl) {
      console.error("Variáveis de ambiente para SSO não foram configuradas corretamente.");
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      return '';
    }

    const url = new URL(`${baseUrl}/${path}/`);
    url.searchParams.set('next', redirectUrl);
    return url.toString();
  }

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      refetch,
      updateAmbient,
      isAuthenticated: state.isAuthenticated,
      isInitialized: state.isInitialized,
      SSOLoginURL: formatSSOUrl('login'),
      logout: logoutMutation,
    }),
    [state.user, state.isAuthenticated, state.isInitialized, logoutMutation, refetch, updateAmbient]
  );

  return <SessionContext.Provider value={memoizedValue}>{children}</SessionContext.Provider>;
}
